type JsonPayload = Record<string, unknown>;

type Fetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

type LogSearchRequestQueueOptions = {
    fetcher?: Fetcher;
    retryDelayMs?: number;
    maxRetries?: number;
};

function createAbortError(): DOMException {
    return new DOMException("The operation was aborted.", "AbortError");
}

function throwIfAborted(signal?: AbortSignal | null): void {
    if (signal?.aborted)
        throw createAbortError();
}

function sleep(ms: number, signal?: AbortSignal | null): Promise<void> {
    return new Promise((resolve, reject) => {
        throwIfAborted(signal);

        const timeout = setTimeout(resolve, ms);

        signal?.addEventListener("abort", () => {
            clearTimeout(timeout);
            reject(createAbortError());
        }, { once: true });
    });
}

function isBusyResponse(response: Response, payload: JsonPayload): boolean {
    return response.status === 429
        && payload.code === "LOG_SEARCH_SESSION_BUSY";
}

export class LogSearchRequestQueue {
    readonly fetcher: Fetcher;
    readonly retryDelayMs: number;
    readonly maxRetries: number;
    private tail: Promise<unknown>;

    constructor({
        fetcher = (...args) => window.fetch(...args),
        retryDelayMs = 250,
        maxRetries = 8,
    }: LogSearchRequestQueueOptions = {}) {
        this.fetcher = fetcher;
        this.retryDelayMs = retryDelayMs;
        this.maxRetries = maxRetries;
        this.tail = Promise.resolve();
    }

    fetchJson<TPayload extends JsonPayload = JsonPayload>(url: string, options: RequestInit = {}): Promise<TPayload> {
        const signal = options.signal;
        const run = () => this.fetchJsonWithRetry<TPayload>(url, options);
        const scheduled = this.tail.catch(() => undefined).then(() => {
            throwIfAborted(signal);
            return run();
        });

        this.tail = scheduled.catch(() => undefined);

        return scheduled;
    }

    async fetchJsonWithRetry<TPayload extends JsonPayload>(url: string, options: RequestInit): Promise<TPayload> {
        const signal = options.signal;

        for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
            throwIfAborted(signal);

            const response = await this.fetcher(url, options);
            const payload = await response.json().catch(() => ({
                code    : response.status,
                error   : response.statusText,
            })) as TPayload;

            if (!isBusyResponse(response, payload) || attempt >= this.maxRetries)
                return payload;

            await sleep(this.retryDelayMs * (attempt + 1), signal);
        }

        return {
            code    : "LOG_SEARCH_SESSION_BUSY",
            error   : "A log search is already running for this session.",
        } as unknown as TPayload;
    }
}

export const SharedLogSearchRequestQueue = new LogSearchRequestQueue();
